import { TrashIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useState } from 'react'
import { deleteRateIndexValue, getRateIndexValues, setRateIndexValue } from 'services'
import { Modal } from 'stories/components'
import { confirm, formatDate } from 'utils'

import type { IRateIndexValue } from './types'
import { useCanEditRateIndexValues } from './useCanEditRateIndexValues'

/**
 * An index's value history. Each value is effective from its date until the next one, so an
 * adjustment can read what the index was on its own date. Entering a date that already has a
 * value replaces it, which is how a typo is corrected, and a value entered by hand is never
 * overwritten by the daily feed.
 *
 * Opened from Admin > Rate Indexes and from the Index field on a loan. Anyone can read the
 * history; entering values needs Servicing.RateIndexes.Values (or admin).
 */
export const RateIndexValuesModal = ({
  index,
  onClose,
}: {
  index: { key: string; name: string; autoUpdated?: boolean }
  onClose: (changed: boolean) => void
}) => {
  const canEdit = useCanEditRateIndexValues()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [rows, setRows] = useState<IRateIndexValue[]>([])
  const [changed, setChanged] = useState(false)
  const [effectiveDate, setEffectiveDate] = useState('')
  const [value, setValue] = useState('')

  useEffect(() => {
    load()
  }, [])

  const load = async () => {
    setLoading(true)
    try {
      setRows((await getRateIndexValues(index.key)) || [])
    } catch (err: any) {
      setError(err?.message || 'Could not load values')
    } finally {
      setLoading(false)
    }
  }

  const onAdd = async () => {
    setError('')
    if (!effectiveDate || value.trim() === '') {
      setError('Enter an effective date and a value')
      return
    }
    setLoading(true)
    try {
      await setRateIndexValue(index.key, { effectiveDate, value })
      setChanged(true)
      setValue('')
      await load()
    } catch (err: any) {
      setError(err?.message || 'Could not save the value')
      setLoading(false)
    }
  }

  const onDelete = async (row: IRateIndexValue) => {
    const isContinue = await confirm(`Delete the ${row.value}% value effective ${formatDate(row.effectiveDate)}?`)
    if (!isContinue) return
    setLoading(true)
    try {
      await deleteRateIndexValue(index.key, row.id)
      setChanged(true)
      await load()
    } catch (err: any) {
      setError(err?.message || 'Could not delete the value')
      setLoading(false)
    }
  }

  return (
    <Modal title={`${index.name} - Values`} titleOkay="" onClose={() => onClose(changed)} isOpen>
      <div className="w-144 relative mt-2 mb-3">
        <LayoutLoading show={loading} />
        {index.autoUpdated && (
          <p className="text-xs text-gray-500 mb-3">
            Updated automatically each morning from the Federal Reserve (FRED). Only dates the value changed are listed.
            A value you enter here takes precedence over the feed for that date.
          </p>
        )}
        {canEdit && (
          <div className="flex items-end gap-3 mb-4">
            <div>
              <div className="text-[13px] text-gray-500 mb-1">Effective Date</div>
              <input
                type="date"
                className="text-[14px] border rounded px-2 py-2"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
              />
            </div>
            <div>
              <div className="text-[13px] text-gray-500 mb-1">Value (%)</div>
              <input
                type="number"
                step="0.0001"
                className="text-[14px] border rounded px-2 py-2 w-32"
                value={value}
                placeholder="7.50"
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onAdd()}
              />
            </div>
            <button
              className="text-[14px] rounded px-4 py-2 bg-shade-blue text-white hover:opacity-90"
              onClick={onAdd}
              disabled={loading}
            >
              Add
            </button>
          </div>
        )}
        {error && <div className="text-sm text-red-700 mb-3">{error}</div>}

        <div className="max-h-[50vh] overflow-auto">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-2">Effective Date</th>
                <th className="px-3 py-2">Value</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {!rows.length && (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-center text-gray-400">
                    No values entered yet
                  </td>
                </tr>
              )}
              {rows.map((row) => (
                <tr key={row.id} className="border-b">
                  <td className="px-3 py-2 text-gray-900">{formatDate(row.effectiveDate)}</td>
                  <td className="px-3 py-2 text-gray-900">{row.value}%</td>
                  <td className="px-3 py-2">{row.source === 'feed' ? 'Federal Reserve feed' : row.createdBy}</td>
                  <td className="px-3 py-2">
                    {canEdit && (
                      <span className="text-red-800 p-1 hover-shadow1 cursor-pointer" onClick={() => onDelete(row)}>
                        <TrashIcon className="w-4 h-4" />
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  )
}
